// Stock sales: shared logic for recording and editing sales (Stocks page and P&L Report).
// Selling takes the oldest shares first (FIFO). Purchase lots live in
// stocks_investments and are reduced or deleted when shares are sold; each sale is a
// row in stock_sales with its average buy price and average buy date.

// Local calendar date as yyyy-MM-dd (toISOString would shift to UTC, a day early in India)
export const toDateString = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// FIFO pick of `units` from purchase lots (oldest first), without changing anything.
// lots: [{ id, purchase_date, price, units }]. Returns what the units cost, their
// cost-weighted average buy date, what's left of each lot, and any shortfall.
export const planFIFOSale = (lots, units) => {
  const sorted = [...lots].sort((a, b) => new Date(a.purchase_date) - new Date(b.purchase_date));
  let remaining = units;
  let cost = 0;
  let weightedTime = 0;
  const changes = [];
  for (const lot of sorted) {
    if (remaining <= 1e-9) break;
    const lotUnits = parseFloat(lot.units);
    const take = Math.min(lotUnits, remaining);
    if (take <= 0) continue;
    const lotCost = take * parseFloat(lot.price);
    cost += lotCost;
    weightedTime += lotCost * new Date(`${String(lot.purchase_date).slice(0, 10)}T00:00:00`).getTime();
    changes.push({ lot, left: lotUnits - take });
    remaining -= take;
  }
  return {
    cost,
    avgBuyDate: cost > 0 ? toDateString(new Date(weightedTime / cost)) : null,
    changes,
    shortBy: remaining > 1e-9 ? remaining : 0,
  };
};

// The units an existing sale took, as a lot to put back: at the sale's average buy
// price and date (the sale date for sales recorded before buy dates were stored)
export const lotFromSale = (sale) => ({
  id: null,
  purchase_date: sale.avg_buy_date || sale.sale_date,
  price: parseFloat(sale.average_buy_price),
  units: parseFloat(sale.units_sold),
});

const isMissingBuyDateColumn = (error) => error
  && /avg_buy_date/i.test(`${error.message} ${error.details || ''}`)
  && ['PGRST204', '42703'].includes(error.code);

// Update a stock_sales row; if the database doesn't have avg_buy_date yet
// (database/2026-10-28_stock_sales_avg_buy_date.sql), save without it
export const updateSaleRecord = async (supabase, saleId, values) => {
  let { error } = await supabase.from('stock_sales').update(values).eq('id', saleId);
  if (isMissingBuyDateColumn(error) && 'avg_buy_date' in values) {
    const { avg_buy_date, ...withoutBuyDate } = values;
    ({ error } = await supabase.from('stock_sales').update(withoutBuyDate).eq('id', saleId));
  }
  return error;
};

// Insert a stock_sales row, with the same fallback
export const insertSaleRecord = async (supabase, values) => {
  let { error } = await supabase.from('stock_sales').insert([values]);
  if (isMissingBuyDateColumn(error)) {
    const { avg_buy_date, ...withoutBuyDate } = values;
    ({ error } = await supabase.from('stock_sales').insert([withoutBuyDate]));
  }
  return error;
};

// Net received and profit/loss for an edited sale, before saving.
// sale: the stock_sales row; lots: the stock's current purchase lots.
export const previewSaleEdit = (sale, lots, { units, price, charges }) => {
  if (!sale || !(units > 0) || !(price > 0)) return null;
  const oldUnits = parseFloat(sale.units_sold);
  const avgBuy = parseFloat(sale.average_buy_price);
  let cost;
  let shortBy = 0;
  if (Math.abs(units - oldUnits) <= 1e-9 && avgBuy > 0) {
    cost = avgBuy * units;
  } else {
    const plan = planFIFOSale(avgBuy > 0 ? [...lots, lotFromSale(sale)] : lots, units);
    cost = plan.cost;
    shortBy = plan.shortBy;
  }
  const net = price * units - (charges || 0);
  return { net, profitLoss: net - cost, shortBy };
};

// Save an edited sale. Same units: only price/date/charges and P&L change.
// Different units: the sale's units go back with the current holdings and the new
// number is taken again, oldest first. Returns { unitsChanged, unitsDifference }.
export const saveSaleEdit = async ({ supabase, userId, saleId, edits }) => {
  const { units: newUnits, price: newPrice, charges = 0, date } = edits;

  const { data: originalSale, error: fetchError } = await supabase
    .from('stock_sales')
    .select('*')
    .eq('id', saleId)
    .single();
  if (fetchError) throw fetchError;

  const oldUnits = parseFloat(originalSale.units_sold);
  const unitsChanged = Math.abs(newUnits - oldUnits) > 1e-9;
  const totalAmount = newPrice * newUnits;
  const netAmount = totalAmount - charges;
  const saleFields = {
    sale_date: toDateString(date),
    units_sold: newUnits,
    sale_price: newPrice,
    total_amount: totalAmount,
    charges,
    net_amount: netAmount,
  };

  if (!unitsChanged) {
    const costBasis = parseFloat(originalSale.average_buy_price) * newUnits;
    const updateError = await updateSaleRecord(supabase, saleId, { ...saleFields, profit_loss: netAmount - costBasis });
    if (updateError) throw updateError;
    return { unitsChanged: false, unitsDifference: 0 };
  }

  const { data: lots, error: lotsError } = await supabase
    .from('stocks_investments')
    .select('*')
    .eq('user_id', userId)
    .eq('name', originalSale.stock_name);
  if (lotsError) throw lotsError;

  const restoredLot = lotFromSale(originalSale);
  const plan = planFIFOSale([...(lots || []), restoredLot], newUnits);
  if (plan.shortBy > 0) {
    throw new Error(`You only have ${(newUnits - plan.shortBy).toFixed(2)} units of ${originalSale.stock_name} available for this sale.`);
  }

  const updateError = await updateSaleRecord(supabase, saleId, {
    ...saleFields,
    profit_loss: netAmount - plan.cost,
    average_buy_price: plan.cost / newUnits,
    avg_buy_date: plan.avgBuyDate,
  });
  if (updateError) throw updateError;

  // Apply the holdings changes: the restored lot keeps only what wasn't re-sold
  const restoredChange = plan.changes.find(c => c.lot === restoredLot);
  const restoredLeft = restoredChange ? restoredChange.left : restoredLot.units;
  if (restoredLeft > 1e-9) {
    const { error: insertError } = await supabase
      .from('stocks_investments')
      .insert([{
        user_id: userId,
        name: originalSale.stock_name,
        ticker: null,
        purchase_date: restoredLot.purchase_date,
        price: restoredLot.price,
        amount_invested: restoredLot.price * restoredLeft,
        units: restoredLeft,
      }]);
    if (insertError) throw insertError;
  }

  for (const { lot, left } of plan.changes) {
    if (lot === restoredLot) continue;
    if (left <= 1e-9) {
      const { error } = await supabase.from('stocks_investments').delete().eq('id', lot.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('stocks_investments')
        .update({ units: left, amount_invested: (lot.amount_invested / parseFloat(lot.units)) * left })
        .eq('id', lot.id);
      if (error) throw error;
    }
  }

  return { unitsChanged: true, unitsDifference: newUnits - oldUnits };
};

// Delete a recorded sale and put its units back into holdings as one purchase at the
// sale's average buy price and buy date (the original purchases were reduced or
// deleted when the sale was made, so they can't be rebuilt exactly).
// Returns { stockName, unitsRestored }.
export const deleteSale = async ({ supabase, userId, saleId }) => {
  const { data: sale, error: fetchError } = await supabase
    .from('stock_sales')
    .select('*')
    .eq('id', saleId)
    .single();
  if (fetchError) throw fetchError;

  const lot = lotFromSale(sale);

  // Restore first, then delete the sale; if the delete fails, undo the restore so
  // holdings and sales stay consistent
  const { data: restored, error: restoreError } = await supabase
    .from('stocks_investments')
    .insert([{
      user_id: userId,
      name: sale.stock_name,
      ticker: null,
      purchase_date: lot.purchase_date,
      price: lot.price,
      amount_invested: lot.price * lot.units,
      units: lot.units,
    }])
    .select('id');
  if (restoreError) throw restoreError;

  const { error: deleteError } = await supabase.from('stock_sales').delete().eq('id', saleId);
  if (deleteError) {
    const restoredId = restored?.[0]?.id;
    if (restoredId) await supabase.from('stocks_investments').delete().eq('id', restoredId);
    throw deleteError;
  }

  return { stockName: sale.stock_name, unitsRestored: lot.units };
};
